export interface PlaceVenueDraft {
  placeId: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  phone: string | null;
  website: string | null;
  businessType: string;
  businessHours: Record<string, { open: string; close: string; closed: boolean }>;
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const PLACE_TYPE_TO_BUSINESS: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  bar: 'Bar',
  night_club: 'Club',
  bakery: 'Bakery',
  meal_takeaway: 'Takeaway',
  meal_delivery: 'Takeaway',
  food: 'Restaurant',
};

const emptyHours = (): PlaceVenueDraft['businessHours'] => {
  const hours: PlaceVenueDraft['businessHours'] = {};
  WEEK_DAYS.forEach(day => {
    hours[day] = { open: '09:00', close: '17:00', closed: true };
  });
  return hours;
};

const formatGoogleTime = (time?: string): string => {
  if (!time || time.length < 4) return '09:00';
  return `${time.slice(0, 2)}:${time.slice(2, 4)}`;
};

export const mapGoogleOpeningHours = (
  openingHours?: google.maps.places.PlaceOpeningHours | null
): PlaceVenueDraft['businessHours'] => {
  const hours = emptyHours();
  const periods = openingHours?.periods;
  if (!periods?.length) return hours;

  periods.forEach(period => {
    const dayIndex = period.open?.day;
    if (dayIndex === undefined || dayIndex === null) return;
    const dayName = DAY_NAMES[dayIndex];
    if (!dayName) return;

    if (!period.close) {
      hours[dayName] = { open: '00:00', close: '23:59', closed: false };
      return;
    }

    hours[dayName] = {
      open: formatGoogleTime(period.open?.time),
      close: formatGoogleTime(period.close?.time),
      closed: false,
    };
  });

  return hours;
};

export const mapGoogleTypesToBusinessType = (types?: string[] | null): string => {
  if (!types?.length) return 'Other';
  for (const type of types) {
    const mapped = PLACE_TYPE_TO_BUSINESS[type];
    if (mapped) return mapped;
  }
  return 'Other';
};

export const placeToVenueDraft = (place: google.maps.places.PlaceResult): PlaceVenueDraft => {
  const placeId = place.place_id;
  const name = place.name?.trim();
  const address = place.formatted_address?.trim();
  const lat = place.geometry?.location?.lat();
  const lng = place.geometry?.location?.lng();

  if (!placeId || !name || !address || lat === undefined || lng === undefined) {
    throw new Error('That place is missing a name, address, or location. Try another result.');
  }

  return {
    placeId,
    name,
    address,
    latitude: lat,
    longitude: lng,
    phone: place.international_phone_number || place.formatted_phone_number || null,
    website: place.website || null,
    businessType: mapGoogleTypesToBusinessType(place.types),
    businessHours: mapGoogleOpeningHours(place.opening_hours),
  };
};

export const normalizeVenueText = (text: string | null | undefined): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/\bstreet\b/g, 'st')
    .replace(/\broad\b/g, 'rd')
    .replace(/\bavenue\b/g, 'ave')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export interface VenueDuplicateCandidate {
  id: string;
  name?: string | null;
  businessName?: string | null;
  address?: string | null;
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  location?: {
    placeId?: string | null;
    formattedAddress?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
}

const coordsAreClose = (
  lat1?: number | null,
  lng1?: number | null,
  lat2?: number | null,
  lng2?: number | null
): boolean => {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return false;
  const dlat = lat1 - lat2;
  const dlng = lng1 - lng2;
  return Math.sqrt(dlat * dlat + dlng * dlng) < 0.001;
};

export const findDuplicateVenue = (
  venues: VenueDuplicateCandidate[],
  draft: PlaceVenueDraft
): VenueDuplicateCandidate | undefined => {
  const draftName = normalizeVenueText(draft.name);
  const draftAddress = normalizeVenueText(draft.address);

  return venues.find(venue => {
    const existingPlaceId = venue.location?.placeId || venue.placeId;
    if (existingPlaceId && existingPlaceId === draft.placeId) return true;

    const existingName = normalizeVenueText(venue.name || venue.businessName);
    if (!draftName || existingName !== draftName) return false;

    const existingAddress = normalizeVenueText(venue.address || venue.location?.formattedAddress);
    if (
      draftAddress &&
      existingAddress &&
      (existingAddress === draftAddress ||
        existingAddress.includes(draftAddress) ||
        draftAddress.includes(existingAddress))
    ) {
      return true;
    }

    return coordsAreClose(
      venue.latitude ?? venue.location?.latitude,
      venue.longitude ?? venue.location?.longitude,
      draft.latitude,
      draft.longitude
    );
  });
};

export class DuplicateVenueError extends Error {
  venueId: string;
  venueName: string;

  constructor(venueId: string, venueName: string) {
    super(`"${venueName}" is already in the venues list.`);
    this.name = 'DuplicateVenueError';
    this.venueId = venueId;
    this.venueName = venueName;
  }
}
